import React, { useEffect, useState } from 'react';
import { Save, CheckCircle2, XCircle, MessageCircle, Mail, Loader2 } from 'lucide-react';
import { authHeaders } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SettingsPanel — configures who receives lead notifications.
 *
 * Kept intentionally small: two fields (recipient email + WhatsApp
 * number for the 1-tap forwarding button in Pascal's email). The env
 * vars stay as a fallback if the fields are cleared — that's shown
 * inline so the admin never wonders "where does this go now?".
 */
const SettingsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [envEmail, setEnvEmail] = useState('');
  const [flash, setFlash] = useState(null);

  const refresh = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/settings/notifications`, {
        headers: authHeaders(),
      });
      if (r.ok) {
        const d = await r.json();
        setEmail(d.recipient_email || '');
        setWhatsapp(d.whatsapp_number || '');
        setEnvEmail(d.env_recipient_email || '');
      }
    } catch (_) { /* keep last */ }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setFlash(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/settings/notifications`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          recipient_email: email.trim() || null,
          whatsapp_number: whatsapp.trim() || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Opslaan mislukt');
      setEmail(d.recipient_email || '');
      setWhatsapp(d.whatsapp_number || '');
      setFlash({ type: 'success', text: 'Notificatie-instellingen opgeslagen. Nieuwe leads gaan nu naar deze ontvanger.' });
    } catch (err) {
      setFlash({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={styles.loading}><Loader2 size={20} className="spin" /> Laden…</div>;

  return (
    <div data-testid="settings-panel">
      <div style={styles.header}>
        <h2 style={styles.h2}>Notificatie-instellingen</h2>
        <p style={styles.sub}>
          Bepaal wie de e-mail krijgt zodra er een nieuwe lead binnenkomt. Handig als
          Pascal op vakantie is — schakel dan tijdelijk een collega in zonder de code
          aan te raken.
        </p>
      </div>

      {flash && (
        <div
          style={{ ...styles.flash, ...(flash.type === 'error' ? styles.flashError : styles.flashOk) }}
          data-testid={`settings-flash-${flash.type}`}
        >
          {flash.type === 'error' ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
          <span>{flash.text}</span>
        </div>
      )}

      <form onSubmit={save} style={styles.card}>
        <label style={styles.field}>
          <span style={styles.label}><Mail size={13} /> Ontvanger e-mail *</span>
          <input
            type="email"
            style={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="pascal@nelemans.es"
            required
            data-testid="settings-recipient-email"
          />
          <span style={styles.help}>
            Elke nieuwe lead genereert een gebrande e-mail naar dit adres met alle
            details en een <em>Reply-To</em> op de lead zelf.
            {envEmail && envEmail !== email && (
              <> Standaard (uit <code>.env</code>): <strong>{envEmail}</strong>.</>
            )}
          </span>
        </label>

        <label style={styles.field}>
          <span style={styles.label}><MessageCircle size={13} /> WhatsApp-nummer voor 1-tap forward</span>
          <input
            type="tel"
            style={styles.input}
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder="+31 6 44 87 94 15"
            data-testid="settings-whatsapp-number"
          />
          <span style={styles.help}>
            Optioneel. Als ingevuld, verschijnt in élke lead-notificatie mail een groene
            &quot;Open WhatsApp met deze lead&quot;-knop die WhatsApp opent met alle
            lead-details voor-ingevuld. Perfect om binnen 1 tik door te sturen naar
            een makelaar. Formaat: internationaal, bijv. <code>+31644879415</code>.
          </span>
        </label>

        <div style={styles.infoBox} data-testid="settings-info-hot">
          <strong style={{ display: 'block', marginBottom: '0.35rem' }}>
            🔥 Hot leads
          </strong>
          Aanvragen met type <em>Bezichtiging</em> of <em>Terugbelverzoek</em>
          krijgen automatisch een rode &quot;Hot lead&quot;-banner en een 🔥
          in de onderwerpregel, zodat je ze meteen ziet in je inbox.
        </div>

        <div style={styles.infoBox} data-testid="settings-info-ack">
          <strong style={{ display: 'block', marginBottom: '0.35rem' }}>
            💬 Automatisch antwoord aan de lead
          </strong>
          Elke lead krijgt direct een gebrande bevestigingsmail met de belofte
          &quot;Pascal neemt binnen 24 uur contact op&quot;. Dat voorkomt onrust
          en houdt de lead warm terwijl Pascal dispatcht. Geen extra
          configuratie nodig.
        </div>

        <button
          type="submit"
          style={styles.btnPrimary}
          disabled={saving || !email.trim()}
          data-testid="settings-save-btn"
        >
          {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
          Opslaan
        </button>
      </form>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  loading: { display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#585755', padding: '2rem' },
  header: { marginBottom: '1.5rem' },
  h2: { fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', margin: 0, color: '#1A1918' },
  sub: { color: '#585755', fontSize: '0.9rem', margin: '0.35rem 0 0', lineHeight: 1.6, maxWidth: '640px' },

  flash: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.9rem' },
  flashOk: { background: '#DCFCE7', color: '#166534', border: '1px solid #86EFAC' },
  flashError: { background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5' },

  card: { background: 'white', border: '1px solid #EAE5DD', padding: '1.75rem', maxWidth: '720px' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1.25rem' },
  label: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', fontWeight: 600, color: '#1A1918', letterSpacing: '0.05em', textTransform: 'uppercase' },
  input: { padding: '0.65rem 0.85rem', border: '1px solid #D8D3CA', fontSize: '0.95rem', background: 'white', boxSizing: 'border-box', width: '100%' },
  help: { fontSize: '0.8rem', color: '#8C8B88', lineHeight: 1.55 },

  infoBox: { background: '#FAF6EE', border: '1px solid #EAE5DD', padding: '0.85rem 1rem', fontSize: '0.85rem', color: '#585755', lineHeight: 1.6, marginBottom: '1rem' },

  btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.7rem 1.25rem', background: '#1A1918', color: '#C5A059', border: 'none', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer' },
};

export default SettingsPanel;
